/**
 * Expected lift from trade spend, read off `metadata.trade_lift` on customer hierarchy nodes.
 *
 * The Confido trade calendar importer (`server/src/integration/confido/trade_calendar.rs`)
 * writes one promoted-volume plan per customer: planned spend, the base/incremental/overlapping
 * decomposition of promoted revenue and units, a per-tactic roll-up, and the underlying events.
 * It lives in metadata for the same reason `trade_spend` does — hierarchy aggregation overwrites
 * a parent's `sales_data` from its children, so anything stored as data points is wiped on the
 * first roll-up and would double-count into revenue.
 *
 * Money is summed in whole cents so a bridge that ought to tie out does. Units are summed as
 * floats and rounded once at the end — they are counts, not currency, and a hundredth of a can
 * never matters.
 */

import { parse_period_key_to_time_period } from "./periods"

/** Period (`YYYY-MM`, or `YYYY-Www` on week-grain versions) → decimal string, as the importer writes it. */
type PeriodValues = Record<string, string>

export interface TradeLiftTacticMetadata {
  spend?: PeriodValues
  lift_revenue?: PeriodValues
  lift_units?: PeriodValues
  event_count?: number
}

export interface TradeLiftEventMetadata {
  id?: string
  /** `Promo Id` — matches `promotion.remote_promo_id`, so the row can link to the promo calendar. */
  remote_promo_id?: string | null
  name?: string
  tactic?: string
  gl_item?: string | null
  ship_to?: string[]
  product_families?: string[]
  start?: string
  end?: string
  accrual_period?: string | null
  spend?: string
  lift_revenue?: string
  lift_units?: string
  base_units?: string
  roi?: string | null
  rate?: string | null
}

export interface TradeLiftMetadata {
  source?: string
  group?: string
  match?: string
  share?: string
  spend?: PeriodValues
  base_revenue?: PeriodValues
  incremental_revenue?: PeriodValues
  overlapping_revenue?: PeriodValues
  gross_revenue?: PeriodValues
  base_units?: PeriodValues
  lift_units?: PeriodValues
  overlapping_units?: PeriodValues
  total_units?: PeriodValues
  by_tactic?: Record<string, TradeLiftTacticMetadata>
  events?: TradeLiftEventMetadata[]
  /** Written when one plan customer received slices of more than one planning group. */
  combined?: TradeLiftMetadata[]
}

export interface TradeLiftPeriodRow {
  period: string
  spend: number
  base_revenue: number
  lift_revenue: number
  incremental_revenue: number
  overlapping_revenue: number
  promoted_revenue: number
  lift_units: number
  base_units: number
  /** Lift revenue per dollar of spend. `null` when the month plans no spend. */
  roi: number | null
}

export interface TradeLiftTacticRow {
  tactic: string
  spend: number
  lift_revenue: number
  lift_units: number
  event_count: number
  roi: number | null
  /** Share of total planned spend, as a percentage. */
  spend_share_pct: number
}

export interface TradeLiftCustomerRow {
  customer: string
  /** The Confido planning group this came from — differs from `customer` on a banner split. */
  group: string
  /** Fraction of that group's plan this customer carries, as a percentage. */
  share_pct: number
  spend: number
  lift_revenue: number
  lift_units: number
  roi: number | null
  event_count: number
  events: TradeLiftEventRow[]
}

export interface TradeLiftEventRow {
  id: string
  /** `null` for calendars exported before the long id was carried through. */
  remote_promo_id: string | null
  name: string
  tactic: string
  customer: string
  start: string
  end: string
  rate: string | null
  spend: number
  lift_revenue: number
  lift_units: number
  roi: number | null
}

export interface TradeLiftSummary {
  spend: number
  base_revenue: number
  /** Incremental + overlapping: everything Confido credits to the promotion beyond base. */
  lift_revenue: number
  /** Lift Confido attributes to this promotion alone. */
  incremental_revenue: number
  /** Lift Confido attributes to overlapping promotions running at the same time. */
  overlapping_revenue: number
  /** Base + lift: the revenue Confido credits to promoted periods. */
  promoted_revenue: number
  lift_units: number
  /** Lift revenue per dollar of spend across the window. */
  roi: number | null
  /** Spend ÷ promoted revenue, as a percentage. */
  promoted_trade_rate_pct: number | null
  /** Lift revenue ÷ promoted revenue, as a percentage — how much of promoted volume is bought. */
  lift_share_pct: number | null
  periods: TradeLiftPeriodRow[]
  tactics: TradeLiftTacticRow[]
  customers: TradeLiftCustomerRow[]
  /** Largest events by planned spend, across every customer. */
  top_events: TradeLiftEventRow[]
  /**
   * Every `remote_promo_id` on screen, deduped — the batch the caller resolves against the promo
   * calendar to decide which rows can link.
   */
  remote_promo_ids: string[]
  /**
   * Distinct promotions, not rows. A banner planning group is split across its regional plan
   * customers, so the same promotion appears on each slice — counting slices reported Sanzo's
   * 594-event calendar as 1,216 promotions.
   */
  event_count: number
}

function to_cents(value: string | number | undefined | null): number {
  if (value == null) return 0
  const n = typeof value === "number" ? value : parseFloat(value)
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

function to_number(value: string | number | undefined | null): number {
  if (value == null) return 0
  const n = typeof value === "number" ? value : parseFloat(value)
  return Number.isFinite(n) ? n : 0
}

function sum_cents(values: PeriodValues | undefined, periods?: Set<string>): number {
  if (!values) return 0
  let total = 0
  for (const [period, value] of Object.entries(values)) {
    if (periods && !periods.has(period)) continue
    total += to_cents(value)
  }
  return total
}

function sum_units(values: PeriodValues | undefined, periods?: Set<string>): number {
  if (!values) return 0
  let total = 0
  for (const [period, value] of Object.entries(values)) {
    if (periods && !periods.has(period)) continue
    total += to_number(value)
  }
  return total
}

function ratio(numerator_cents: number, denominator_cents: number): number | null {
  if (denominator_cents === 0) return null
  return numerator_cents / denominator_cents
}

/** Pull `metadata.trade_lift` off a node, flattening the multi-group `combined` shape. */
export function read_trade_lift(node: unknown): TradeLiftMetadata[] {
  const metadata = (node as { metadata?: unknown } | null)?.metadata
  if (!metadata || typeof metadata !== "object") return []
  const trade_lift = (metadata as { trade_lift?: unknown }).trade_lift
  if (!trade_lift || typeof trade_lift !== "object") return []
  const typed = trade_lift as TradeLiftMetadata
  if (Array.isArray(typed.combined) && typed.combined.length > 0) return typed.combined
  return [typed]
}

/** True when any node in the forest carries a trade-lift plan — gates the whole mode. */
export function hierarchy_has_trade_lift(roots: unknown[]): boolean {
  let found = false
  function walk(node: any): void {
    if (found) return
    if (read_trade_lift(node).length > 0) {
      found = true
      return
    }
    for (const child of (node?.children ?? []) as unknown[]) walk(child)
  }
  for (const root of roots) walk(root)
  return found
}

/**
 * The calendar months the visible periods cover, for date-overlap tests against events.
 *
 * Month keys map to themselves; week keys map to their ISO-Thursday month (via
 * `parse_period_key_to_time_period`), so a week-grain grid still compares in month space —
 * comparing an event's `2027-04` month prefix against `2027-W14` lexicographically silently
 * failed (`"W" > "0"`), which zeroed the Promos column on every weekly version.
 */
export function period_month_keys(periods?: Set<string>): Set<string> | undefined {
  if (!periods) return undefined
  const months = new Set<string>()
  for (const period of periods) {
    const parsed = parse_period_key_to_time_period(period)
    if (Number.isFinite(parsed.month) && parsed.month >= 1 && parsed.month <= 12) {
      months.add(`${parsed.year}-${String(parsed.month).padStart(2, "0")}`)
    }
  }
  return months.size > 0 ? months : undefined
}

/**
 * An event overlaps the window when its promotion dates touch any month on screen.
 *
 * The event's own `spend` is its whole-promotion figure, so a straddling event is listed once
 * rather than pro-rated — the period rows above it already carry the exact phased dollars.
 */
function event_in_periods(event: TradeLiftEventMetadata, months?: Set<string>): boolean {
  if (!months) return true
  const start = (event.start ?? "").slice(0, 7)
  const end = (event.end ?? "").slice(0, 7)
  if (!start && !end) return true
  for (const month of months) {
    if ((!start || month >= start) && (!end || month <= end)) return true
  }
  return false
}

/**
 * Build the trade-lift view for the periods currently on screen.
 *
 * `periods` scopes every roll-up to the visible grid columns, so the panel and the grid always
 * describe the same window. Returns `null` when no node carries a lift plan, which is what keeps
 * the mode hidden for brands that have never imported a trade calendar.
 */
export function build_trade_lift(roots: unknown[], periods?: string[]): TradeLiftSummary | null {
  const period_filter = periods && periods.length > 0 ? new Set(periods) : undefined
  // Events carry promotion DATES, not period keys, so their window test runs in month space
  // regardless of the grid's grain.
  const event_month_filter = period_month_keys(period_filter)

  const period_cents = new Map<
    string,
    {
      spend: number
      base: number
      lift: number
      incremental: number
      overlapping: number
      lift_units: number
      base_units: number
    }
  >()
  const tactic_cents = new Map<string, { spend: number; lift: number; lift_units: number; events: number }>()
  const customers: TradeLiftCustomerRow[] = []
  const all_events: TradeLiftEventRow[] = []
  // Distinct promotions; a banner split repeats the same id on every regional slice.
  const distinct_event_ids = new Set<string>()
  let found_any = false

  function empty_period_row() {
    return { spend: 0, base: 0, lift: 0, incremental: 0, overlapping: 0, lift_units: 0, base_units: 0 }
  }

  function accumulate_periods(
    values: PeriodValues | undefined,
    key: "spend" | "base" | "incremental" | "overlapping"
  ): void {
    for (const [period, value] of Object.entries(values ?? {})) {
      if (period_filter && !period_filter.has(period)) continue
      const row = period_cents.get(period) ?? empty_period_row()
      const cents = to_cents(value)
      row[key] += cents
      // `lift` is the pair's total; keeping it alongside the components means the panel can
      // disclose the split without the rows ever disagreeing with the headline.
      if (key === "incremental" || key === "overlapping") row.lift += cents
      period_cents.set(period, row)
    }
  }

  function accumulate_period_units(values: PeriodValues | undefined, key: "lift_units" | "base_units"): void {
    for (const [period, value] of Object.entries(values ?? {})) {
      if (period_filter && !period_filter.has(period)) continue
      const row = period_cents.get(period) ?? empty_period_row()
      row[key] += to_number(value)
      period_cents.set(period, row)
    }
  }

  function visit(node: any): void {
    for (const lift of read_trade_lift(node)) {
      found_any = true
      const customer_name = String(node?.name ?? "Unknown")

      accumulate_periods(lift.spend, "spend")
      accumulate_periods(lift.base_revenue, "base")
      accumulate_periods(lift.incremental_revenue, "incremental")
      accumulate_periods(lift.overlapping_revenue, "overlapping")
      accumulate_period_units(lift.lift_units, "lift_units")
      accumulate_period_units(lift.overlapping_units, "lift_units")
      accumulate_period_units(lift.base_units, "base_units")

      for (const [tactic, values] of Object.entries(lift.by_tactic ?? {})) {
        const spend = sum_cents(values.spend, period_filter)
        const lift_revenue = sum_cents(values.lift_revenue, period_filter)
        const lift_units = sum_units(values.lift_units, period_filter)
        if (spend === 0 && lift_revenue === 0 && lift_units === 0) continue
        const acc = tactic_cents.get(tactic) ?? { spend: 0, lift: 0, lift_units: 0, events: 0 }
        acc.spend += spend
        acc.lift += lift_revenue
        acc.lift_units += lift_units
        acc.events += values.event_count ?? 0
        tactic_cents.set(tactic, acc)
      }

      const customer_spend = sum_cents(lift.spend, period_filter)
      const customer_lift = sum_cents(lift.incremental_revenue, period_filter) + sum_cents(lift.overlapping_revenue, period_filter)
      const customer_lift_units = sum_units(lift.lift_units, period_filter) + sum_units(lift.overlapping_units, period_filter)

      const events: TradeLiftEventRow[] = (lift.events ?? [])
        .filter((event) => event_in_periods(event, event_month_filter))
        .map((event) => {
          const spend = to_cents(event.spend)
          const lift_revenue = to_cents(event.lift_revenue)
          return {
            id: String(event.id ?? ""),
            remote_promo_id: event.remote_promo_id ?? null,
            name: String(event.name ?? event.id ?? "Promotion"),
            tactic: String(event.tactic ?? "Unclassified"),
            customer: customer_name,
            start: String(event.start ?? ""),
            end: String(event.end ?? ""),
            rate: event.rate ?? null,
            spend: spend / 100,
            lift_revenue: lift_revenue / 100,
            lift_units: to_number(event.lift_units),
            roi: ratio(lift_revenue, spend),
          }
        })
        .sort((a, b) => b.spend - a.spend || a.name.localeCompare(b.name))

      all_events.push(...events)
      for (const event of events) distinct_event_ids.add(event.id || `${customer_name}:${event.name}`)

      if (customer_spend !== 0 || customer_lift !== 0 || events.length > 0) {
        customers.push({
          customer: customer_name,
          group: String(lift.group ?? customer_name),
          share_pct: to_number(lift.share ?? "1") * 100,
          spend: customer_spend / 100,
          lift_revenue: customer_lift / 100,
          lift_units: customer_lift_units,
          roi: ratio(customer_lift, customer_spend),
          event_count: events.length,
          events,
        })
      }
    }
    for (const child of (node?.children ?? []) as unknown[]) visit(child)
  }

  for (const root of roots) visit(root)
  if (!found_any) return null

  const period_rows: TradeLiftPeriodRow[] = [...period_cents.entries()]
    .map(([period, row]) => ({
      period,
      spend: row.spend / 100,
      base_revenue: row.base / 100,
      lift_revenue: row.lift / 100,
      incremental_revenue: row.incremental / 100,
      overlapping_revenue: row.overlapping / 100,
      promoted_revenue: (row.base + row.lift) / 100,
      lift_units: Math.round(row.lift_units),
      base_units: Math.round(row.base_units),
      roi: ratio(row.lift, row.spend),
    }))
    .sort((a, b) => a.period.localeCompare(b.period))

  const spend_cents = period_rows.reduce((sum, row) => sum + Math.round(row.spend * 100), 0)
  const base_cents = period_rows.reduce((sum, row) => sum + Math.round(row.base_revenue * 100), 0)
  const lift_cents = period_rows.reduce((sum, row) => sum + Math.round(row.lift_revenue * 100), 0)
  const lift_units = period_rows.reduce((sum, row) => sum + row.lift_units, 0)
  const incremental_cents = period_rows.reduce((sum, row) => sum + Math.round(row.incremental_revenue * 100), 0)
  const overlapping_cents = period_rows.reduce((sum, row) => sum + Math.round(row.overlapping_revenue * 100), 0)

  const tactics: TradeLiftTacticRow[] = [...tactic_cents.entries()]
    .map(([tactic, acc]) => ({
      tactic,
      spend: acc.spend / 100,
      lift_revenue: acc.lift / 100,
      lift_units: Math.round(acc.lift_units),
      event_count: acc.events,
      roi: ratio(acc.lift, acc.spend),
      spend_share_pct: spend_cents === 0 ? 0 : (acc.spend / spend_cents) * 100,
    }))
    .sort((a, b) => b.spend - a.spend || a.tactic.localeCompare(b.tactic))

  customers.sort((a, b) => b.spend - a.spend || a.customer.localeCompare(b.customer))

  return {
    spend: spend_cents / 100,
    base_revenue: base_cents / 100,
    lift_revenue: lift_cents / 100,
    incremental_revenue: incremental_cents / 100,
    overlapping_revenue: overlapping_cents / 100,
    promoted_revenue: (base_cents + lift_cents) / 100,
    lift_units,
    roi: ratio(lift_cents, spend_cents),
    promoted_trade_rate_pct: base_cents + lift_cents === 0 ? null : (spend_cents / (base_cents + lift_cents)) * 100,
    lift_share_pct: base_cents + lift_cents === 0 ? null : (lift_cents / (base_cents + lift_cents)) * 100,
    periods: period_rows,
    tactics,
    customers,
    top_events: [...all_events].sort((a, b) => b.spend - a.spend || a.name.localeCompare(b.name)).slice(0, 25),
    remote_promo_ids: [...new Set(all_events.map((event) => event.remote_promo_id).filter(Boolean) as string[])],
    event_count: distinct_event_ids.size,
  }
}
